import { useState, useCallback } from 'react';
import { toast } from 'sonner';

import { useBulkProcessConsignmentsMutation, type CancellationReason } from '@/entities/consignments';
import { useProjectSiteNavigation } from '@/shared/lib';

import { processBulkResult } from '../lib/bulk-process-utils';
import { RouteNames } from '@/shared/config/routes';

interface UseConsignmentActionsProps {
  selectedIds?: string[];
  onClearSelection?: () => void;
}

export function useConsignmentActions({ selectedIds = [], onClearSelection }: UseConsignmentActionsProps = {}) {
  const { projectId, siteId, navigateWithContext } = useProjectSiteNavigation();
  const [bulkProcess, { isLoading }] = useBulkProcessConsignmentsMutation();

  const [changePriorityOpen, setChangePriorityOpen] = useState(false);
  const [placeOnHoldOpen, setPlaceOnHoldOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [showDispositionOpen, setShowDispositionOpen] = useState(false);

  const [localSelectedIds, setLocalSelectedIds] = useState<string[] | null>(null);

  const handleEditView = useCallback(
    (ids?: string[]) => {
      const consignmentIds = ids && ids.length > 0 ? ids : selectedIds;
      if (consignmentIds.length > 0) {
        navigateWithContext(
          RouteNames.CONSIGNMENT_EDIT,
          { consignmentId: consignmentIds[0] },
          {
            state: { consignmentIds }
          }
        );
      }
    },
    [selectedIds, navigateWithContext]
  );

  const handleShowDisposition = useCallback((ids?: string[]) => {
    const idsToUse = ids && ids.length > 0 ? ids : null;
    setLocalSelectedIds(idsToUse);
    setShowDispositionOpen(true);
  }, []);

  const handleChangePriority = useCallback((ids?: string[]) => {
    const idsToUse = ids && ids.length > 0 ? ids : null;
    setLocalSelectedIds(idsToUse);
    setChangePriorityOpen(true);
  }, []);

  const handlePlaceOnHold = useCallback((ids?: string[]) => {
    const idsToUse = ids && ids.length > 0 ? ids : null;
    setLocalSelectedIds(idsToUse);
    setPlaceOnHoldOpen(true);
  }, []);

  const handleCancel = useCallback((ids?: string[]) => {
    const idsToUse = ids && ids.length > 0 ? ids : null;
    setLocalSelectedIds(idsToUse);
    setCancelOpen(true);
  }, []);

  const confirmChangePriority = useCallback(
    async (priority: string, comments: string) => {
      if (!projectId || !siteId) {
        toast.error('Missing project or site parameters');
        return;
      }
      const consignmentIds = localSelectedIds || selectedIds;

      try {
        const result = await bulkProcess({
          projectId,
          siteId,
          consignmentIds,
          action: 'CHANGE_PRIORITY',
          payload: {
            priority,
            comment: comments
          }
        }).unwrap();

        processBulkResult({
          result,
          successMessage: (count) => `Priority changed for ${count} consignment(s)`,
          errorMessage: 'Failed to change priority for all selected consignments',
          warningMessage: (succeeded, total) => `Priority changed for ${succeeded} of ${total} consignments`
        });

        onClearSelection?.();
        setChangePriorityOpen(false);
        setLocalSelectedIds(null);
      } catch (error) {
        toast.error('Failed to change priority');
        console.error('Failed to change priority:', error);
      }
    },
    [projectId, siteId, localSelectedIds, selectedIds, bulkProcess, onClearSelection]
  );

  const confirmPlaceOnHold = useCallback(
    async (remarks: string) => {
      if (!projectId || !siteId) {
        toast.error('Missing project or site parameters');
        return;
      }

      const consignmentIds = localSelectedIds || selectedIds;

      try {
        const result = await bulkProcess({
          projectId,
          siteId,
          consignmentIds,
          action: 'SET_ON_HOLD',
          payload: {
            comment: remarks
          }
        }).unwrap();

        processBulkResult({
          result,
          successMessage: (count) => `${count} consignment(s) placed on hold`,
          errorMessage: 'Failed to place all selected consignments on hold',
          warningMessage: (succeeded, total) => `${succeeded} of ${total} consignments placed on hold`
        });

        onClearSelection?.();
        setPlaceOnHoldOpen(false);
        setLocalSelectedIds(null);
      } catch (error) {
        toast.error('Failed to place on hold');
        console.error('Failed to place on hold:', error);
      }
    },
    [projectId, siteId, localSelectedIds, selectedIds, bulkProcess, onClearSelection]
  );

  const confirmCancel = useCallback(
    async (reason: string, remarks: string) => {
      if (!projectId || !siteId) {
        toast.error('Missing project or site parameters');
        return;
      }

      const consignmentIds = localSelectedIds || selectedIds;

      try {
        const result = await bulkProcess({
          projectId,
          siteId,
          consignmentIds,
          action: 'CANCEL',
          payload: {
            reason: reason as CancellationReason,
            comment: remarks
          }
        }).unwrap();

        processBulkResult({
          result,
          successMessage: (count) => `${count} consignment(s) cancelled`,
          errorMessage: 'Failed to cancel all selected consignments',
          warningMessage: (succeeded, total) => `${succeeded} of ${total} consignments cancelled`
        });

        onClearSelection?.();
        setCancelOpen(false);
        setLocalSelectedIds(null);
      } catch (error) {
        toast.error('Failed to cancel consignments');
        console.error('Failed to cancel:', error);
      }
    },
    [projectId, siteId, localSelectedIds, selectedIds, bulkProcess, onClearSelection]
  );

  return {
    actions: {
      editView: handleEditView,
      showDisposition: handleShowDisposition,
      changePriority: handleChangePriority,
      placeOnHold: handlePlaceOnHold,
      cancel: handleCancel
    },
    dialogs: {
      showDisposition: {
        open: showDispositionOpen,
        onOpenChange: setShowDispositionOpen,
        consignmentId: localSelectedIds?.[0] || selectedIds?.[0] || null
      },
      changePriority: {
        open: changePriorityOpen,
        onOpenChange: setChangePriorityOpen,
        onConfirm: confirmChangePriority
      },

      placeOnHold: {
        open: placeOnHoldOpen,
        onOpenChange: setPlaceOnHoldOpen,
        onConfirm: confirmPlaceOnHold
      },
      cancel: {
        open: cancelOpen,
        onOpenChange: setCancelOpen,
        onConfirm: confirmCancel
      }
    },
    selectedCount: selectedIds.length,
    isLoading
  };
}
